import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { Download } from '@mui/icons-material';
import mammoth from 'mammoth';
import * as XLSX from 'xlsx';

const DocumentViewer = ({ filePath, fileType, onLoad }) => {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  
  // Create a key to force re-render when file changes
  const fileKey = `${filePath}-${fileType}`;

  useEffect(() => {
    // Reset state when file changes
    setLoading(true);
    setError(false);
    
    // Clear container content
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
    }
    
    const loadDocument = async () => {
      try {
        // Handle different file types
        if (fileType === 'docx') {
          await loadDocx(filePath);
        } else if (fileType === 'xlsx' || fileType === 'xls') {
          await loadExcel(filePath);
        } else if (fileType === 'csv') {
          await loadCsv(filePath);
        } else if (fileType === 'txt') {
          await loadTxt(filePath);
        } else if (fileType === 'ppt' || fileType === 'pptx') {
            await loadPowerPoint(filePath);
        } else {
          // For other file types (including ppt/pptx), we'll show the download option
          console.log("Unsupported file type:", fileType);
          setError(true);
        }
        
        if (!error) {
          setLoading(false);
          onLoad();
        }
      } catch (err) {
        console.error('Error loading document:', err);
        setError(true);
        setLoading(false);
      }
    };
    
    loadDocument();
    
    // Cleanup function to handle component unmount or file changes
    return () => {
      // Clean up any resources or event listeners
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [fileKey, onLoad]); // Use fileKey instead of separate dependencies

  // Load Word document using Mammoth.js
  const loadDocx = async (path) => {
    try {
      const response = await fetch(path);
      const arrayBuffer = await response.arrayBuffer();
      
      if (!containerRef.current) return;
      
      // Clear previous content
      containerRef.current.innerHTML = '';
      
      // Create a container with fixed dimensions and scrolling
      const docxContainer = document.createElement('div');
      docxContainer.className = 'docx-container';
      containerRef.current.appendChild(docxContainer);
      
      // Convert DOCX to HTML using Mammoth
      const result = await mammoth.convertToHtml({ arrayBuffer });
      docxContainer.innerHTML = result.value;
      
      // Add CSS to ensure proper containment and scrolling
      const style = document.createElement('style');
      style.textContent = `
        .docx-container {
          width: 100%;
          height: 100%;
          overflow: auto;
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          padding: 20px;
          box-sizing: border-box;
          background-color: white;
        }
        
        .docx-container * {
          max-width: 100%;
          box-sizing: border-box;
        }
        
        .docx-container p {
          margin-bottom: 1em;
          line-height: 1.5;
        }
        
        .docx-container h1, 
        .docx-container h2, 
        .docx-container h3, 
        .docx-container h4, 
        .docx-container h5, 
        .docx-container h6 {
          margin-top: 1.5em;
          margin-bottom: 0.5em;
          font-weight: bold;
        }
        
        .docx-container table {
          border-collapse: collapse;
          margin: 1em 0;
          width: auto;
          max-width: 100%;
        }
        
        .docx-container table, 
        .docx-container th, 
        .docx-container td {
          border: 1px solid #ddd;
        }
        
        .docx-container th, 
        .docx-container td {
          padding: 8px;
          text-align: left;
        }
        
        .docx-container img {
          max-width: 100%;
          height: auto;
        }
        
        .docx-container ul, 
        .docx-container ol {
          margin-left: 2em;
          margin-bottom: 1em;
        }
        
        .docx-container li {
          margin-bottom: 0.5em;
        }
        
        /* Add a max-width to create a more document-like appearance */
        .docx-container > * {
          max-width: 800px;
          margin-left: auto;
          margin-right: auto;
        }
      `;
      containerRef.current.appendChild(style);
    } catch (error) {
      console.error('Error loading Word document:', error);
      setError(true);
    }
  };

  // Load Excel document
  const loadExcel = async (path) => {
    try {
      const response = await fetch(path);
      const arrayBuffer = await response.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      
      // Get first worksheet
      const firstSheet = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheet];
      
      if (!containerRef.current) return;
      
      // Clear previous content
      containerRef.current.innerHTML = '';
      
      // Create a container with fixed dimensions and scrolling
      const excelContainer = document.createElement('div');
      excelContainer.className = 'excel-container';
      containerRef.current.appendChild(excelContainer);
      
      // Convert to HTML
      const html = XLSX.utils.sheet_to_html(worksheet, {
        id: 'excel-table',
        editable: false
      });
      
      // Insert the HTML into our container
      excelContainer.innerHTML = html;
      
      // Add CSS to ensure proper containment and scrolling
      const style = document.createElement('style');
      style.textContent = `
        .excel-container {
          width: 100%;
          height: 100%;
          overflow: auto;
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          padding: 16px;
          box-sizing: border-box;
        }
        
        #excel-table {
          border-collapse: collapse;
          width: auto;
          font-family: Arial, sans-serif;
          table-layout: fixed;
          margin: 0;
        }
        
        #excel-table td, #excel-table th {
          border: 1px solid #ddd;
          padding: 8px;
          min-width: 100px;
          max-width: 300px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        
        #excel-table th {
          padding-top: 12px;
          padding-bottom: 12px;
          text-align: left;
          background-color: #f2f2f2;
          color: #333;
          font-weight: bold;
          position: sticky;
          top: 0;
          z-index: 10;
        }
        
        #excel-table tr:nth-child(even) {
          background-color: #f9f9f9;
        }
        
        #excel-table tr:hover {
          background-color: #f1f1f1;
        }
      `;
      containerRef.current.appendChild(style);
    } catch (error) {
      console.error('Error loading Excel document:', error);
      setError(true);
    }
  };

  // Load CSV file
  const loadCsv = async (path) => {
    try {
      const response = await fetch(path);
      const text = await response.text();
      
      if (!containerRef.current) return;
      
      // Clear previous content
      containerRef.current.innerHTML = '';
      
      // Create a container with fixed dimensions and scrolling
      const csvContainer = document.createElement('div');
      csvContainer.className = 'csv-container';
      containerRef.current.appendChild(csvContainer);
      
      // Parse CSV and create a table
      const rows = text.split('\n');
      let tableHtml = '<table id="csv-table">';
      
      // Add header row if available
      if (rows.length > 0) {
        const headers = rows[0].split(',');
        tableHtml += '<thead><tr>';
        headers.forEach(header => {
          tableHtml += `<th>${header.trim().replace(/"/g, '')}</th>`;
        });
        tableHtml += '</tr></thead>';
      }
      
      // Add data rows
      tableHtml += '<tbody>';
      for (let i = 1; i < rows.length; i++) {
        if (rows[i].trim() === '') continue;
        
        const cells = rows[i].split(',');
        tableHtml += '<tr>';
        cells.forEach(cell => {
          tableHtml += `<td>${cell.trim().replace(/"/g, '')}</td>`;
        });
        tableHtml += '</tr>';
      }
      tableHtml += '</tbody></table>';
      
      csvContainer.innerHTML = tableHtml;
      
      // Add CSS to ensure proper containment and scrolling
      const style = document.createElement('style');
      style.textContent = `
        .csv-container {
          width: 100%;
          height: 100%;
          overflow: auto;
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          padding: 16px;
          box-sizing: border-box;
        }
        
        #csv-table {
          border-collapse: collapse;
          width: auto;
          font-family: Arial, sans-serif;
          table-layout: fixed;
          margin: 0;
        }
        
        #csv-table td, #csv-table th {
          border: 1px solid #ddd;
          padding: 8px;
          min-width: 100px;
          max-width: 300px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        
        #csv-table th {
          padding-top: 12px;
          padding-bottom: 12px;
          text-align: left;
          background-color: #f2f2f2;
          color: #333;
          font-weight: bold;
          position: sticky;
          top: 0;
          z-index: 10;
        }
        
        #csv-table tr:nth-child(even) {
          background-color: #f9f9f9;
        }
        
        #csv-table tr:hover {
          background-color: #f1f1f1;
        }
      `;
      containerRef.current.appendChild(style);
    } catch (error) {
      console.error('Error loading CSV file:', error);
      setError(true);
    }
  };

  // Load TXT file
  const loadTxt = async (path) => {
    try {
      const response = await fetch(path);
      const text = await response.text();
      
      if (!containerRef.current) return;
      
      // Clear previous content
      containerRef.current.innerHTML = '';
      
      // Create a container with fixed dimensions and scrolling
      const txtContainer = document.createElement('div');
      txtContainer.className = 'txt-container';
      containerRef.current.appendChild(txtContainer);
      
      // Create pre element to preserve formatting
      const preElement = document.createElement('pre');
      preElement.className = 'txt-content';
      preElement.textContent = text;
      txtContainer.appendChild(preElement);
      
      // Add CSS to ensure proper containment and scrolling
      const style = document.createElement('style');
      style.textContent = `
        .txt-container {
          width: 100%;
          height: 100%;
          overflow: auto;
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          padding: 20px;
          box-sizing: border-box;
          background-color: white;
        }
        
        .txt-content {
          font-family: 'Courier New', monospace;
          white-space: pre-wrap;
          word-wrap: break-word;
          margin: 0;
          padding: 0;
          font-size: 14px;
          line-height: 1.5;
          max-width: 100%;
        }
      `;
      containerRef.current.appendChild(style);
    } catch (error) {
      console.error('Error loading text file:', error);
      setError(true);
    }
  };

  // Load PowerPoint file with a simple viewer
// Load PowerPoint file
const loadPowerPoint = (path) => {
    if (containerRef.current) {
        // Clear previous content
        containerRef.current.innerHTML = '';

        // Create a container for the viewer
        const viewerContainer = document.createElement('div');
        viewerContainer.className = 'office-viewer-container';
        containerRef.current.appendChild(viewerContainer);

        // Get the absolute URL for the PowerPoint file
        let absoluteUrl = path;
        
        // If the path is relative, convert it to absolute
        if (!path.startsWith('http://') && !path.startsWith('https://')) {
            // Get the base URL of your application
            const baseUrl = window.location.origin;
            absoluteUrl = `${baseUrl}${path.startsWith('/') ? '' : '/'}${path}`;
        }
        
        // Encode the URL for the Office Online Viewer
        const encodedUrl = encodeURIComponent(absoluteUrl);
        const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`;
        
        // Create the iframe for the Office Online Viewer
        const iframe = document.createElement('iframe');
            iframe.src = officeViewerUrl;
            iframe.className = 'office-viewer-iframe';
            iframe.width = '100%';
            iframe.height = '100%';
            iframe.frameBorder = '0';
            iframe.allowFullscreen = true;
            viewerContainer.appendChild(iframe);
            
        // Add a loading indicator
        const loadingDiv = document.createElement('div');
            loadingDiv.className = 'office-viewer-loading';
            loadingDiv.innerHTML = `
                <div class="loading-spinner"></div>
                <p>Loading PowerPoint presentation...</p>
            `;
            viewerContainer.appendChild(loadingDiv);
            
        // Add a fallback message and download button in case the viewer fails
        const fallbackDiv = document.createElement('div');
            fallbackDiv.className = 'office-viewer-fallback';
            fallbackDiv.innerHTML = `
                <div class="fallback-content">
                <h3>Unable to preview presentation</h3>
                <p>If the presentation doesn't load, you can download it to view in PowerPoint.</p>
                <button id="download-ppt" class="download-btn">Download Presentation</button>
                </div>
            `;
        viewerContainer.appendChild(fallbackDiv);
        
        // Add event listeners
        iframe.addEventListener('load', () => {
            // Hide loading indicator when iframe loads
            loadingDiv.style.display = 'none';
            
            // Check if the iframe loaded successfully or shows an error page
            try {
                // This will throw an error if the iframe content is from a different origin
                const iframeContent = iframe.contentWindow.document.body.innerHTML;
                
                // If we can access the content, check if it contains error messages
                if (iframeContent.includes('error') || iframeContent.includes('Error')) {
                    fallbackDiv.style.display = 'flex';
                } else {
                    fallbackDiv.style.display = 'none';
                }
            } catch (e) {
                // Cross-origin access error - we can't check the content
                // We'll assume it loaded correctly and hide the fallback
                fallbackDiv.style.display = 'none';
                
                // Set a timeout to check if the Office Viewer shows its own UI
                // If after 5 seconds the loading indicator is still visible, show the fallback
                setTimeout(() => {
                    if (loadingDiv.style.display !== 'none') {
                    loadingDiv.style.display = 'none';
                    fallbackDiv.style.display = 'flex';
                    }
                }, 5000);
            }
            
            // Mark as loaded
            setLoading(false);
        });
        
        // Handle download button click
        const downloadBtn = document.getElementById('download-ppt');
        if (downloadBtn) {
            downloadBtn.addEventListener('click', () => {
            window.open(path, '_blank');
            });
        }
        
        // Add CSS styles
        const style = document.createElement('style');
        style.textContent = `
            .office-viewer-container {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: #f5f5f5;
            }
            
            .office-viewer-iframe {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 1;
            }
            
            .office-viewer-loading {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            background-color: rgba(255, 255, 255, 0.9);
            z-index: 2;
            }
            
            .loading-spinner {
            width: 40px;
            height: 40px;
            border: 4px solid #f3f3f3;
            border-top: 4px solid #3498db;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-bottom: 20px;
            }
            
            @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
            }
            
            .office-viewer-fallback {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            display: none;
            align-items: center;
            justify-content: center;
            background-color: rgba(255, 255, 255, 0.95);
            z-index: 3;
            }
            
            .fallback-content {
            max-width: 500px;
            padding: 30px;
            text-align: center;
            background-color: white;
            border-radius: 8px;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
            }
            
            .download-btn {
            background-color: #2196f3;
            color: white;
            border: none;
            padding: 10px 20px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
            margin-top: 20px;
            }
            
            .download-btn:hover {
            background-color: #1976d2;
            }
        `;
        containerRef.current.appendChild(style);
    }
};

  // Get file type display name
  const getFileTypeName = () => {
    switch (fileType) {
      case 'doc':
      case 'docx':
        return 'Word';
      case 'xls':
      case 'xlsx':
        return 'Excel';
      case 'csv':
        return 'CSV';
      case 'txt':
        return 'Text';
      case 'ppt':
      case 'pptx':
        return 'PowerPoint';
      case 'pdf':
        return 'PDF';
      default:
        return 'Document';
    }
  };
  // If there's an error or unsupported file type, show download option
  if (error || !['docx', 'xlsx', 'xls', 'csv', 'txt', 'ppt', 'pptx'].includes(fileType)) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100%', 
        p: 3, 
        bgcolor: '#fff' 
      }}>
        <Typography variant="h6" gutterBottom>
          {error ? 'Unable to preview this document' : `${getFileTypeName()} preview is not available`}
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', maxWidth: 600 }}>
          This document type may not display properly in the browser. You can download it to view on your device.
        </Typography>
        <Button 
          variant="contained" 
          href={filePath} 
          download 
          onClick={onLoad}
          startIcon={<Download />}
        >
          Download {getFileTypeName()}
        </Button>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        width: '100%', 
        height: '100%', 
        position: 'relative', 
        bgcolor: '#fff',
        overflow: 'hidden'
      }}
    >
      <Box 
        ref={containerRef} 
        sx={{ 
          width: '100%', 
          height: '100%',
          position: 'relative'
        }} 
      />
      
      {loading && (
        <Box sx={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center',
          bgcolor: 'rgba(255, 255, 255, 0.9)',
          zIndex: 20
        }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="body1">Loading document...</Typography>
        </Box>
      )}
    </Box>
  );
};

export default DocumentViewer;